import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { generateHTML, JSONContent } from '@tiptap/core';
import { ANNOUNCEMENT_RICH_TEXT_EXTENSIONS } from '../../../core/components/announcement-list/announcement-rich-text';
import { DeepLink, Notification } from '../../shared/entities/notification/notification';
import { NotificationRowActions } from '../../shared/services/notification/notification-list.store';

/**
 * deepLink.type -> Router-Route (#736 §8). Unbekannte Typen werden ignoriert, nicht geworfen: das
 * Backend kann jederzeit einen neuen Zieltyp einfuehren.
 *
 * Diese Pfade sind zugleich ein Vertrag gegenueber den Mail-Links — der Server setzt
 * /tasks/task/<id> und /issues/issue/<id> fest im Code zusammen (Client-Dok §5.1). Wer sie aendert,
 * macht bereits verschickte Mails ungueltig.
 */
const DEEP_LINK_ROUTES: Record<string, (id: string) => string[]> = {
  TASK: (id) => ['/tasks/task', id],
  ISSUE: (id) => ['/issues/issue', id],
  TOUR: (id) => ['/tour-planner/detail', id]
};

/**
 * Eine Zeile im Benachrichtigungs-Stream.
 *
 * Aufbau seit der UI-Ueberarbeitung vom 19.08.2026 (Vorbild: der Benachrichtigungs-Screen der
 * Mobile-App) sind drei Baender:
 *
 *   Titelzeile — Korrektur-Kennzeichnung, Titel, rechts die Verwaltungsgesten (gelesen, loeschen)
 *   Inhalt     — bodyRich gerendert, sonst body
 *   Fusszeile  — Relativzeit links, die typabhaengige Aktion rechts
 *
 * Der Breitenmodus (`wide`) ist damit entfallen: es gibt nur noch DIESES Layout. Die Seite
 * /notifications benutzt dieselbe Zeile — sie ist seit derselben Ueberarbeitung nicht mehr
 * verlinkt, ein zweites Layout nur fuer sie zu pflegen zahlt sich nicht.
 */
@Component({
  selector: "app-notification",
  templateUrl: "notification.html",
  styleUrls: ["./notification.scss"]
})
export class NotificationComponent {

  @Input() notification: Notification;

  /**
   * Die Liste, zu der diese Zeile gehoert. Bewusst ein Input und nicht der Popover-Store aus dem
   * Service: sonst wuerde ein Klick auf der Seite /notifications die Liste des Popovers patchen.
   *
   * Typisiert auf die schmale Schnittstelle NotificationRowActions statt NotificationListStore
   * direkt: dashboard-announcements.component erfuellt sie ebenfalls, laedt seine Zeilen aber ueber
   * eine eigene Query statt diesen Store.
   */
  @Input() list: NotificationRowActions;

  /**
   * Feuert, wenn diese Zeile tatsaechlich navigiert hat. Das Popover haengt daran und schliesst
   * sich — Klicks INNERHALB eines p-overlayPanel loesen seinen Dismiss-Listener grundsaetzlich
   * nicht aus, das Overlay erfaehrt sonst nie, dass es den Screen gewechselt hat.
   *
   * Bewusst nicht ueber NavigationEnd im Layout geloest: bei `onSameUrlNavigation: 'ignore'`
   * (Default) feuert kein Navigationsereignis, wenn das Ziel die bereits offene URL ist.
   *
   * Beim Wegwischen bleibt das Popover offen — man will mehrere Zeilen nacheinander abraeumen.
   */
  @Output() navigated = new EventEmitter<void>();

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
  }

  /**
   * Der Farbbalken links ersetzt den Punkt im Text und folgt dem Muster von
   * app-task-row-v2-component. Gelesene Zeilen bekommen keinen Balken, behalten aber seine Breite,
   * damit der Text nicht springt.
   */
  get barColor(): string {
    return this.notification?.readAt ? 'transparent' : '#DAFF00';
  }

  /**
   * Eine gelesene Zeile tritt komplett zurueck, nicht nur ihre Schriftstaerke (Vorbild App).
   *
   * Bewusst auf die Textbloecke gelegt und nicht auf die ganze Zeile: die Knoepfe sollen ihren
   * vollen Kontrast behalten, sonst sieht ein gedimmter Papierkorb aus wie ein deaktivierter.
   */
  get dimClass(): string {
    return this.notification?.readAt ? 'opacity-60' : '';
  }

  /** Ungelesenes traegt den Titel fett — zusaetzlich zum Balken, fuer sich allein zu leise. */
  get titleWeightClass(): string {
    return this.notification?.readAt ? 'font-light' : 'font-medium';
  }

  /**
   * Rich-Text der Ankuendigungen, EINMAL bei Eingangswechsel gerendert.
   *
   * Bewusst ein Getter und kein `ngOnChanges`: die bestehende Spec dieser Komponente setzt
   * Inputs per direkter Zuweisung auf die Instanz, nicht ueber `componentRef.setInput` —
   * Angular loest `ngOnChanges` dabei nicht aus, ein Lifecycle-Hook liefe dort also nie. Der
   * Getter funktioniert unabhaengig davon, wie der Input gesetzt wurde.
   *
   * Die Komponente laeuft ohne OnPush: ein UNMEMOISIERTER Getter wuerde bei jedem
   * Change-Detection-Zyklus neu parsen — bei 20 Zeilen im Stream spuerbar. Deshalb die
   * Memoisierung auf die Baum-Referenz (`renderedFor`/`rendered` unten).
   *
   * Gerendert mit DERSELBEN Extension-Liste, die der Editor benutzt — Client-Dok §11: „ein
   * Renderer, der mehr kann, als der Server erlaubt, verdeckt Fehler statt sie zu zeigen."
   * Insbesondere NICHT mit vollem StarterKit.
   *
   * bodyRich ist ein Baum, kein Markup. Wir ERZEUGEN daraus HTML, statt es als HTML zu
   * behandeln (§11).
   *
   * Seit der Ueberarbeitung vom 19.08.2026 auch im Popover: die Liste scrollt jetzt endlos
   * (Infinite Scroll), eine lange Ankuendigung schiebt also nichts mehr aus einem festen
   * Sichtfeld — der Grund fuer die frueher nur breite Darstellung ist damit weg.
   */
  private renderedFor: JSONContent | null = null;
  private rendered: SafeHtml | null = null;

  get bodyHtml(): SafeHtml | null {
    const tree = this.notification?.bodyRich ?? null;
    if (tree === this.renderedFor) {
      return this.rendered;
    }
    this.renderedFor = tree;
    this.rendered = tree ? this.renderRich(tree) : null;
    return this.rendered;
  }

  private renderRich(tree: JSONContent): SafeHtml | null {
    try {
      // bypassSecurityTrustHtml ist hier sicher, WEIL generateHTML mit exakt derselben
      // Extension-Liste laeuft, die auch der Editor benutzt: Link.renderHTML blankt ein
      // unzulaessiges href aus (isAllowedUri in announcement-rich-text.ts), ein
      // javascript:-Ziel rendert also als <a href="">, nie als ausfuehrbarer Link. Die
      // Sicherheit dieses Aufrufs haengt also an einer Zeile in einer anderen Datei — wer
      // ANNOUNCEMENT_RICH_TEXT_EXTENSIONS aendert, muss das mitdenken.
      return this.sanitizer.bypassSecurityTrustHtml(
        generateHTML(tree, ANNOUNCEMENT_RICH_TEXT_EXTENSIONS)
      );
    } catch {
      // Ein Baum, den unsere Extensions nicht kennen, darf die Zeile nicht sprengen — dann
      // greift der Klartext-Rueckfall.
      return null;
    }
  }

  /**
   * Hat die Zeile ueberhaupt ein Ziel? Nur dann steht in der Fusszeile ein Aktionsknopf.
   *
   * Frueher trug dieser Knopf im zielllosen Fall „Als gelesen markieren". Das macht jetzt der
   * Haken in der Titelzeile — und zwar bei JEDER ungelesenen Zeile, nicht nur bei den ziellosen.
   */
  get hasAction(): boolean {
    return !!(this.notification?.deepLink || this.notification?.uriLink);
  }

  /**
   * Zwei Gesten an einem Knopf (#736 §7, uriLink seit Client-Dok §8.7). Was die Zeile anbietet,
   * entscheidet `deepLink`/`uriLink` — nie beide gleichzeitig gesetzt, siehe Notification-Entity —,
   * und genau daran haengt auch die Beschriftung im Template (`detailsLabel`):
   *
   * - mit deepLink  -> "Details"        -> intern navigieren -> notificationOpened(id, IN_APP)
   * - mit uriLink   -> "Mehr erfahren"  -> extern oeffnen    -> notificationOpened(id, IN_APP)
   *
   * Gemeldet wird auch bei einem unbekannten deepLink.type: der Nutzer HAT die Zeile angeklickt,
   * dass wir das Ziel nicht kennen, ist unser Problem und nicht seins.
   */
  onDetailsClicked(notification: Notification) {
    if (notification?.deepLink) {
      this.list.reportOpened(notification.id);
      this.navigateToDeepLink(notification.deepLink);
      return;
    }
    if (notification?.uriLink) {
      this.list.reportOpened(notification.id);
      this.openExternal(notification.uriLink);
    }
    // Ohne Ziel gibt es keinen Knopf, der hier landet — und schon gar kein stilles Wegwischen.
  }

  /** Beschriftung des Aktionsknopfs — siehe onDetailsClicked() fuer die zwei Faelle. */
  get detailsLabel(): string {
    return this.notification?.uriLink
      ? 'notificationComponent.learnMore'
      : 'notificationComponent.showDetails';
  }

  /**
   * Haken in der Titelzeile: gelesen, ohne zu oeffnen (Client-Dok §3). Steht nur an ungelesenen
   * Zeilen — bei sticky ausdruecklich ebenfalls erlaubt, anders als das Loeschen.
   */
  onMarkReadClicked(notification: Notification) {
    this.list.markRead(notification.id);
  }

  /**
   * Wegraeumen ohne Wiederherstellen (Client-Dok §3). Bewusst ohne Rueckfrage: das Dok rahmt es als
   * Wegwisch-Geste, und die TTL raeumt den Eintrag nach 7 Tagen ohnehin.
   */
  onDeleteClicked(notification: Notification) {
    this.list.deleteNotification(notification.id);
  }

  private navigateToDeepLink(deepLink: DeepLink): void {
    const buildRoute = DEEP_LINK_ROUTES[deepLink.type];
    if (!buildRoute) {
      console.warn('notification: unbekannter deepLink.type', deepLink.type);
      return;
    }
    // Das Ziel darf geloescht sein oder der PermissionGuard darf umleiten (#736 §8) — der
    // Ziel-Screen zeigt dann seinen Leerzustand. Eine abgewiesene Navigation darf aber keine
    // unbehandelte Rejection hinterlassen.
    this.router
      .navigate(buildRoute(deepLink.id))
      .catch((error) => console.warn('notification: Navigation abgewiesen', error));
    this.navigated.emit();
  }

  /**
   * Client-Dok §8.7: eigener Tab, `rel="noopener"` ausdruecklich gefordert — ohne das dritte
   * Argument haelt window.open() sonst einen window.opener-Verweis der geoeffneten Seite auf diese
   * Seite offen. Anders als ein interner Deep-Link bleibt das Popover offen: es wurde nicht
   * innerhalb der eigenen App navigiert.
   */
  private openExternal(uriLink: string): void {
    window.open(uriLink, '_blank', 'noopener');
  }

}
